/**
 * What 3 Ingredients - SEO & Schema.org Recipe Injector
 * Dynamically injects structured JSON-LD recipe data into document head
 * so Google and search engines generate rich snippets (cook time, yield, ingredients).
 */

import { scaleQuantity } from './portionScaler.js';

/**
 * Updates or creates the JSON-LD recipe schema in <head>
 * @param {object} recipe 
 * @param {number} currentServings 
 */
export function injectRecipeSchema(recipe, currentServings = 2) {
  if (!recipe) return;

  let scriptEl = document.getElementById('jsonld-recipe-schema');
  if (!scriptEl) {
    scriptEl = document.createElement('script');
    scriptEl.id = 'jsonld-recipe-schema';
    scriptEl.type = 'application/ld+json';
    document.head.appendChild(scriptEl);
  }

  // Format ingredients list for schema
  const ingredientStrings = [];

  // Main ingredients
  recipe.mainIngredients.forEach(ing => {
    const qtyObj = recipe.baseQuantities ? recipe.baseQuantities[ing] : null;
    if (qtyObj) {
      const scaled = scaleQuantity(qtyObj.qty, recipe.servings, currentServings);
      ingredientStrings.push(`${scaled} ${qtyObj.unit || ''} ${ing}`.trim());
    } else {
      ingredientStrings.push(ing);
    }
  });

  // Utility ingredients
  if (recipe.utilityIngredients) {
    recipe.utilityIngredients.forEach(u => {
      const scaled = scaleQuantity(u.qty, recipe.servings, currentServings);
      ingredientStrings.push(`${scaled} ${u.unit || ''} ${u.name}`.trim());
    });
  }

  // ISO 8601 duration parser helper
  const parseMinutesToISO = (timeStr) => {
    const match = (timeStr || '').match(/\d+/);
    const mins = match ? match[0] : '10';
    return `PT${mins}M`;
  };

  const schemaData = {
    '@context': 'https://schema.org/',
    '@type': 'Recipe',
    'name': recipe.title,
    'description': recipe.tagline,
    'author': {
      '@type': 'Organization',
      'name': 'What 3 Ingredients'
    },
    'prepTime': parseMinutesToISO(recipe.prepTime),
    'cookTime': parseMinutesToISO(recipe.cookTime),
    'totalTime': parseMinutesToISO(recipe.prepTime),
    'recipeYield': `${currentServings} servings`,
    'recipeCategory': 'Quick 3-Ingredient Meal',
    'recipeCuisine': recipe.region || 'Global',
    'keywords': '3 ingredient recipes, simple food ideas, fast cooking, pantry recipes',
    'recipeIngredient': ingredientStrings,
    'recipeInstructions': (recipe.steps || []).map((step, idx) => ({
      '@type': 'HowToStep',
      'position': idx + 1,
      'text': step
    }))
  };

  scriptEl.textContent = JSON.stringify(schemaData, null, 2);
}
